<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_PLACEMENT')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="PlacementRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="placementList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: minimum amount  -->
      <template #cell(expectedReturn)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <!-- Column: maximum tranx -->
      <!-- <template #cell(expectedReturn)="data">
        <p class="mb-0">
          {{ toAmount(data.value ) }}
        </p>
      </template> -->

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('UPDATE_PLACEMENT')"
              @click="onSelectPlacementItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectPlacementItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!data.item.referenceNumber && data.item.status === 'Active'"
              @click="onDisburse(data.item, $event.target)"
            >
              <feather-icon icon="CreditCardIcon" />
              <span class="align-middle ml-50">
                {{ $t('Disburse') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status === 'Active'"
              @click="onSelectLiquidatePlacement(data.item, $event.target)"
            >
              <feather-icon icon="RefreshCwIcon" />
              <span class="align-middle ml-50">{{ $t('Liquidate') }}</span>
            </b-dropdown-item>
          </b-dropdown></div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="placementRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row>
            <b-col
              cols="12"
              class="mb-0"
            >
              <h5 class="section-label mb-2">
                {{ $t('Placement Information') }}
              </h5>
            </b-col>
          </b-row>
          <b-row class="match-height">
            <!-- Placement Name -->
            <b-col md="6">
              <InputField
                id="placement-name"
                v-model="placementFormData.name"
                :label="$t('Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Name')"
                rules="required"
                for="placement-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Placement Name -->

            <!-- Description -->
            <b-col md="6">
              <InputField
                id="description"
                v-model="placementFormData.description"
                :label="$t('Description')"
                :placeholder="$t('Enter description')"
                type="text"
                :error-name="$t('Description')"
                rules="required"
                for="description"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Description -->
            <!-- Placement Destination -->
            <b-col md="6">
              <SelectField
                id="placement-destination"
                v-model="placementFormData.destination"
                :label="$t('Placement Destination')"
                :options="placementDestinationOptions"
                :error-name="$t('Placement Destination')"
                rules="required"
                item="label"
                :reduce="option => option"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />

            </b-col>
            <!-- Placement Destination -->
            <b-col md="6">
              <span class="mt-0 mb-0">
                {{ $t('Days Per Annum (Accrual)') }}
              </span>
              <b-form-radio-group
                v-model="placementFormData.daysPerAnnum"
                :options="translateDaysPerAnnumOptions"
                class="demo-inline-spacing"
                name="radios-stacked"
                stacked
                value-field="value"
                text-field="text"
                :disabled="isEditable"
                @input="computeExpectedReturn"
              />
            </b-col>

            <!-- Placement Amount -->
            <b-col md="6">
              <InputNumberField
                id="placement-amount"
                v-model="placementFormData.amount"
                :label="$t('Placement Amount')"
                :placeholder="$t('Enter Placement Amount')"
                type="number"
                :error-name="$t('Placement Amount')"
                rules="required|positive"
                for="placement-amount"
                :readonly="isEditable"
                :is-allow-negative-numbers="false"
                @input="computeExpectedReturn"
              />
            </b-col>
            <!-- Placement Amount -->

            <!--  Tenure (Days) -->
            <b-col md="6">
              <b-form-group>
                <InputField
                  id="tenure-in-days"
                  v-model.number="placementFormData.tenure"
                  :label="$t('Tenure (Days)')"
                  :placeholder="$t('Enter Tenure (Days)')"
                  type="number"
                  :error-name="$t('Tenure (Days)')"
                  rules="required|integer|positive"
                  for="tenure-in-days"
                  :readonly="isEditable"
                  @input="computeExpectedReturn"
                />
              </b-form-group>
            </b-col>
            <!--  Tenure (Days) -->

            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="start-date"
                v-model="placementFormData.startDate"
                rules="required"
                :label="$t('Start Date')"
                :on-change="setMaturityDate"
                :error-name="$t('Start Date')"
                :config="datePickerConfig"
                :placeholder="$t('Select Start Date')"
                :disabled="isCustomDisabled"
              />
            </b-col>
            <!-- Start Date -->

            <!--  Maturity Date -->
            <b-col md="6">
              <InputField
                id="maturity-date"
                v-model="placementFormData.maturityDate"
                :label="$t('Maturity Date')"
                type="text"
                :error-name="$t('Maturity Date')"
                rules="required"
                readonly
              />
            </b-col>
            <!--  Maturity Date -->

            <!--  Rate per Annum (%) -->
            <b-col md="6">
              <InputNumberField
                id="rate"
                v-model="placementFormData.rateOfAnnum"
                :label="$t('Rate per Annum (%)')"
                type="text"
                :error-name="$t('Rate per Annum')"
                rules="required|positive"
                for="rate"
                :readonly="isEditable"
                :is-allow-negative-numbers="false"
                @input="computeExpectedReturn"
              />
            </b-col>
            <!--  Rate per Annum (%) -->

            <!--  Expected Return -->
            <b-col md="6">
              <InputField
                id="expected-return"
                v-model="expectedReturn"
                :label="$t('Expected Return')"
                type="text"
                :error-name="$t('Expected Return')"
                rules="required"
                readonly
              />
            </b-col>
            <!--  Expected Return -->
            <!--  Daily Accrual -->
            <b-col md="6">
              <InputField
                id="daily-accrual"
                v-model="dailyAccrual"
                :label="$t('Daily Accrual')"
                type="text"
                :error-name="$t('Daily Accrual')"
                rules="required"
                readonly
              />
            </b-col>
            <!--  Daily Accrual -->
          </b-row>

          <b-row>
            <b-col
              cols="12"
              class="mb-0 mt-2"
            >
              <h5 class="section-label mb-2">
                {{ $t('Reporting Information') }}
              </h5>
            </b-col>
          </b-row>
          <b-row class="match-height">

            <!-- Source GL -->
            <b-col md="6">
              <SelectField
                id="source-gl"
                v-model="placementFormData.sourceGeneralLedger"
                :label="$t('Source GL')"
                :options="placementSourceGLsOptions"
                :error-name="$t('Source GL')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Source GL -->

            <!-- Investment GL -->
            <b-col md="6">
              <SelectField
                id="investment-gl"
                v-model="placementFormData.investmentGeneralLedger"
                :label="$t('Investment GL')"
                :options="placementInvestmentGLsOptions"
                :error-name="$t('Investment GL')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Investment GL -->

            <!-- Interest Receivable GL -->
            <b-col md="6">
              <SelectField
                id="interest-gl"
                v-model="placementFormData.interestReceivableGeneralLedger"
                :label="$t('Interest Receivable GL')"
                :options="placementInterestReceivableGLsOptions"
                :error-name="$t('Interest Receivable GL')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Interest Receivable GL -->

            <!-- Interest Income GL -->
            <b-col md="6">
              <SelectField
                id="interest-income-gl"
                v-model="placementFormData.interestIncomeGeneralLedger"
                :label="$t('Interest Income GL')"
                :options="placementInterestIncomeGLsOptions"
                :error-name="$t('Interest Income GL')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Interest Income GL -->
          </b-row>
          <b-row>
            <b-col
              cols="12"
              class="mb-0 mt-2"
            >
              <h5 class="section-label mb-2">
                {{ $t('Liquidation & Rollover Option') }}
              </h5>
            </b-col>
          </b-row>
          <b-row class="match-height mb-2 mt-2">
            <b-col md="6">
              <SwitchButton
                v-model="placementFormData.applyRollover"
                :label="$t('Apply Rollover')"
                name="apply-rollover"
                :readonly="isReadonly"
                :checked="false"
                :change="setApplyRollover(placementFormData.applyRollover)"
              />
              <span
                v-if="isApplyRollover"
                class="mb-0"
              >
                <b-form-radio-group
                  v-model="placementFormData.roloverOption"
                  :options="translateRolloverTypeOptions"
                  class="demo-inline-spacing"
                  name="radios-stacked1"
                  stacked
                  value-field="value"
                  text-field="text"
                  :disabled="isDisabled"
                />
              </span>
            </b-col>

            <b-col md="6">
              <SwitchButton
                v-model="placementFormData.applyPreLiquidationCharge"
                :label="$t('Apply Pre-liquidation Charge')"
                name="apply-pre-liquidation"
                :readonly="isReadonly"
                :checked="false"
                :change="setApplyPreLiquidationCharge(placementFormData.applyPreLiquidationCharge)"
              />
              <span
                v-if="isApplyPreLiquidation"
                class="mb-0"
              >
                <InputNumberField
                  id="percentage-charge"
                  v-model="placementFormData.percentageCharge"
                  :label="$t('Percentage Charge (%)')"
                  type="text"
                  :readonly="isReadonly"
                  :is-allow-negative-numbers="false"
                />
              </span>
            </b-col>
            <b-col
              class="mt-2"
              md="6"
            >
              <SwitchButton
                v-model="placementFormData.applyWithholdingTax"
                :label="$t('Apply With-holding Tax')"
                name="apply-withholding-tax"
                :readonly="isReadonly"
                :checked="true"
                :change="setApplyWithHoldingTaxRate(placementFormData.applyWithholdingTax)"
              />
              <span
                v-if="isApplyWithHoldingTax"
                class="mb-0"
              >
                <InputNumberField
                  id="withholding-tax-rate"
                  v-model="placementFormData.withholdingTaxRate"
                  :label="$t('With Holding Tax Rate (%)')"
                  type="text"
                  rules="required"
                  :error-name="$t('With Holding Tax Rate')"
                  :readonly="isReadonly"
                  :is-allow-negative-numbers="false"
                />
              </span>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchPlacement"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Reference ID -->
        <b-col md="6">
          <InputField
            id="reference-id"
            v-model="filter.referenceNumber"
            :label="$t('Reference ID')"
            :placeholder="$t('Enter Reference ID')"
            type="text"
          />
        </b-col>
        <!-- Reference ID -->

        <!-- Maturity Date From -->
        <b-col md="6">
          <DatePickerField
            id="maturity-date-from"
            v-model="filter.maturityStartDate"
            rules="required"
            :label="$t('Maturity Date (From)')"
            :error-name="$t('Maturity Date From')"
            :config="datePickerConfig"
            :placeholder="$t('Select Maturity Date From')"
            :readonly="isEditable"
          />
        </b-col>
        <!-- Maturity Date From -->
        <!-- Maturity Date To -->
        <b-col md="6">
          <DatePickerField
            id="maturity-date-to"
            v-model="filter.maturityEndDate"
            rules="required"
            :label="$t('Maturity Date (To)')"
            :error-name="$t('Maturity Date To')"
            :config="datePickerConfig"
            :placeholder="$t('Select Maturity Date To')"
            :readonly="isEditable"
          />
        </b-col>
        <!-- Maturity Date To -->

        <!-- Placement Destination -->
        <b-col md="6">
          <SelectField
            id="placement-destination"
            v-model="filter.destination"
            :label="$t('Placement Destination')"
            :options="placementDestinationOptions"
            :error-name="$t('Placement Destination')"
            rules="required"
            item="label"
            :reduce="option => option"
            :placeholder="$t('Select')"
          />

        </b-col>
        <!-- Placement Destination -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="placementStatusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <!-- Liquidate modal -->
    <b-modal
      id="modal-liquidate"
      ref="my-modal-liquidate"
      :ok-title="$t('Liquidate Placement')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="md"
      :title="$t('Liquidate Placement')"
      modal-footer
      @ok="liquidatePlacement"
      @cancel="resetLiquidate"
    >
      <validation-observer ref="liquidateRules">
        <b-form @submit.prevent="liquidatePlacement">
          <b-row class="match-height">

            <!-- Effective Date -->
            <b-col
              md="12"
              class="mt-2 mb-2"
            >
              <DatePickerField
                id="effective-date"
                v-model="liquidatedPlacement.effectiveDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Effective Date')"
                :error-name="$t('Effective Date')"
                :config="currentFinDatePickerConfig"
                :placeholder="$t('Select Effective Date')"
              />
            </b-col>
          <!-- Effective Date -->
          </b-row>
        </b-form>
      </validation-observer>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>
    <!-- Liquidate modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge,
  BForm, BDropdown, BDropdownItem, BFormRadioGroup, BFormGroup,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  required, amount, positive,
} from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import DatePickerField from '../../forms/DatePickerField.vue'
import {
  toAmount, toBalance, toKobo,
} from '@/helpers/formatAmount'
import { toYearWithDate, toDateWithYear, getMinusOneDayDate } from '../../../helpers/formatDate'
import { placementDestinationSector, placementStatus } from '../../../helpers/data'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import InputNumberField from '../../forms/InputNumberField.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import getEndDate from '../../../helpers/getEndDate'

import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BSpinner,
    BModal,
    BForm,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
    SwitchButton,
    InputNumberField,
    DatePickerField,
    BFormRadioGroup,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      isDisabled: false,
      isCustomDisabled: false,
      isLoading: false,
      isEditable: false,
      required,
      toAmount,
      toBalance,
      amount,
      positive,
      getStatusColor,
      modalTitle: this.$t('Create Placement'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true },
        { key: 'referenceNumber', sortable: true, label: 'Reference ID' },
        { key: 'destination', sortable: true, label: 'Placement Destination' },
        { key: 'amount', sortable: true, label: 'Placement Amount' },
        { key: 'tenure', sortable: true, label: 'Tenure (Days)' },
        { key: 'rateOfAnnum', sortable: true, label: 'Rate per Annum(%)' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'maturityDate', sortable: true, label: 'Maturity Date' },
        { key: 'expectedReturn', sortable: true, label: 'Expected Return' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      placementFormData: {
        name: '',
        description: '',
        destination: '',
        daysPerAnnum: 365,
        amount: '',
        startDate: '',
        tenure: '',
        maturityDate: '',
        rateOfAnnum: '',
        expectedReturn: '',
        dailyAccrual: '',
        sourceGeneralLedger: '',
        investmentGeneralLedger: '',
        interestReceivableGeneralLedger: '',
        interestIncomeGeneralLedger: '',
        applyRollover: false,
        roloverOption: '',
        applyPreLiquidationCharge: false,
        percentageCharge: 0,
        applyWithholdingTax: true,
        withholdingTaxRate: null,
      },
      placementDestinationOptions: placementDestinationSector,
      daysPerAnnumOptions: [
        { text: '365 Days', value: 365 },
        { text: '360 Days', value: 360 },
      ],
      placementSourceGLsOptions: [],
      placementInvestmentGLsOptions: [],
      placementInterestReceivableGLsOptions: [],
      placementInterestIncomeGLsOptions: [],
      permission: '',
      rolloverTypeOptions: [
        { text: 'Rollover Principal Only', value: 'RolloverPrincipalOnly' },
        { text: 'Rollover Principal + Interest', value: 'RolloverPrincipalAndInterest' },
      ],
      placementList: [],
      filter: {
        name: '',
        referenceNumber: '',
        destination: '',
        maturityStartDate: '',
        maturityEndDate: '',
        status: '',
      },
      // placementStatusOptions: placementStatus,
      liquidatedPlacement: {
        effectiveDate: '',
      },
      liquidateFormData: {
        id: '',
        name: '',
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        defaultDate: '',
        ariaDateFormat: 'd/m/Y',
      },
      currentFinDatePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      incomeGlOptions: 'account/incomeGlOptions',
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    getPreviousFinancialDates() {
      return getMinusOneDayDate(this.currentFinancialDate.financialDate)
    },
    expectedReturn: {
      get() {
        return this.placementFormData.expectedReturn
          ? this.placementFormData.expectedReturn.toFixed(2)
          : ''
      },
      set(newValue) {
        return newValue
      },
    },
    dailyAccrual: {
      get() {
        return this.placementFormData.dailyAccrual
          ? this.placementFormData.dailyAccrual.toFixed(2)
          : ''
      },
      set(newValue) {
        return newValue
      },
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    placementStatusOptions() {
      return placementStatus.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translateRolloverTypeOptions() {
      return this.rolloverTypeOptions.map(item => ({ ...item, text: item.text ? this.$t(item.text) : item.value }))
    },
    translateDaysPerAnnumOptions() {
      return this.daysPerAnnumOptions.map(item => ({ ...item, text: item.text ? this.$t(item.text) : item.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPlacement()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPlacement()
    },

  },
  mounted() {
    this.fetchPlacement()
    this.getPermission()
    this.getPlacementSourceGLs()
    this.getPlacementInvestmentGLs()
    this.getPlacementInterestReceivableGL()
    this.getPlacementInterestIncomeGL()
    this.currentFinDatePickerConfig.maxDate = this.getPreviousFinancialDates
  },
  methods: {
    ...mapActions({
      getPlacements: 'account/getPlacementsList',
      updatePlacement: 'account/updatePlacementAction',
      createPlacement: 'account/createPlacementAction',
      getPlacementSourceGeneralLedger: 'account/getPlacementSourceGeneralLedger',
      getPlacementInvestmentGeneralLedger: 'account/getPlacementInvestmentGeneralLedger',
      getPlacementInterestReceivableGLs: 'account/getPlacementInterestReceivableGLs',
      getPlacementInterestIncomeGLs: 'account/getPlacementInterestIncomeGLs',
      liquidatePlacementAction: 'account/liquidatePlacementAction',
      disbursePlacement: 'account/disbursePlacement',
    }),
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = accountPermissions.ADD_PLACEMENT
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.UPDATE_PLACEMENT
        return
      }
      this.permission = accountPermissions.VIEW_PLACEMENT
    },
    async onDisburse(items) {
      const updateStatus = async () => {
        try {
          this.showSpinner = true

          const formData = {
            id: items.id,
            initiatedBy: this.userDetails.email,
            approvedBy: '',
          }

          const response = await this.disbursePlacement({ formData, permission: accountPermissions.ADD_PLACEMENT })
          const { status, data } = response
          if (status === 200 && data.isSuccessful) {
            this.showSpinner = false
            this.fetchPlacement()
            this.alertSuccess({ text: `${this.$t('You have successfully disbursed')} ${toBalance(items.amount)} ${this.$t('to')} ${items.name}` })
          }
          if (status === 200 && !data.isSuccessful) {
            this.alertError({ text: `${this.$t('Your Loan disbursement request failed. Reason')}: ${data.message}` })
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(updateStatus, { title: '', text: `${this.$t('Are you sure you want to Disburse')} ${toBalance(items.amount)} ${this.$t('to')} ${items.name}?` })
    },
    setMaturityDate(dateStr) {
      const selectedDate = Array.isArray(dateStr) ? dateStr[0] : dateStr
      this.placementFormData.maturityDate = getEndDate(selectedDate, this.placementFormData.tenure)
    },

    computeExpectedReturn() {
      const expectedReturned = ((this.placementFormData.amount * (this.placementFormData.rateOfAnnum / 100)) / this.placementFormData.daysPerAnnum) * this.placementFormData.tenure
      this.placementFormData.expectedReturn = expectedReturned
      this.placementFormData.dailyAccrual = (expectedReturned / this.placementFormData.tenure)
    },
    setApplyRollover(selectedValue) {
      this.isApplyRollover = selectedValue
        ? selectedValue === true
        : false
      if (this.isApplyRollover === false) {
        this.placementFormData.roloverOption = ''
      }
    },
    setApplyPreLiquidationCharge(selectedValue) {
      this.isApplyPreLiquidation = selectedValue
        ? selectedValue === true
        : false
      if (this.isApplyPreLiquidation === false) {
        this.placementFormData.percentageCharge = 0
      }
    },
    setApplyWithHoldingTaxRate(selectedValue) {
      this.isApplyWithHoldingTax = selectedValue
        ? selectedValue === true
        : false
      if (this.isApplyWithHoldingTax === false) {
        this.placementFormData.withholdingTaxRate = ''
      }
    },
    async getPlacementSourceGLs() {
      try {
        const response = await this.getPlacementSourceGeneralLedger({ permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.placementSourceGLsOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPlacementInvestmentGLs() {
      try {
        const response = await this.getPlacementInvestmentGeneralLedger({ permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.placementInvestmentGLsOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPlacementInterestReceivableGL() {
      try {
        const response = await this.getPlacementInterestReceivableGLs({ permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.placementInterestReceivableGLsOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPlacementInterestIncomeGL() {
      try {
        const response = await this.getPlacementInterestIncomeGLs({ permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.placementInterestIncomeGLsOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async placementAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.placementFormData,
          expectedReturn: toKobo(this.placementFormData.expectedReturn),
          dailyAccrual: toKobo(this.placementFormData.dailyAccrual),
          startDate: toYearWithDate(this.placementFormData.startDate),
          maturityDate: toYearWithDate(this.placementFormData.maturityDate),
          amount: toKobo(this.placementFormData.amount),
          initiatedBy: this.userDetails.email,
        }
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createPlacement(formData)
          : await this.updatePlacement(formData)

        const { status } = await response
        if (status === 200) {
          this.fetchPlacement()
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Placement was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${text}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Placement failed. Reason:')
          const errorMessage = `${requestToText} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.placementRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const placementText = this.$t('this Placement?')
          this.confirm(this.placementAction, { text: `${areYouSure} ${this.buttonLabel} ${placementText}` })
        }
      })
    },
    onSelectPlacementItem(items, activity, val) {
      try {
        this.isDisabled = val !== 'edit'
        this.isCustomDisabled = val === 'view' || val === 'edit'
        this.isEditable = true
        this.placementFormData = { ...items }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Placement') : this.$t('View Placement')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    onSelectLiquidatePlacement(item) {
      try {
        this.liquidateFormData = {
          id: item.id,
          name: item.name,
        }
        this.modalTitle = this.$t('Liquidate Placement')
        this.buttonLabel = this.$t('Liquidated')
        this.$root.$emit('bv::show::modal', 'modal-liquidate')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async liquidatePlacement(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.liquidateRules.validate().then(async success => {
        if (success) {
          try {
            this.showSpinner = true
            const formData = {
              id: this.liquidateFormData.id,
              name: this.liquidateFormData.name,
              initiatedBy: this.userDetails.email,
              effectiveDate: this.liquidatedPlacement.effectiveDate,
            }
            const permission = 'VIEW_PLACEMENT'
            const response = await this.liquidatePlacementAction({ formData, permission })

            const { status } = await response
            if (status === 200) {
              this.fetchPlacement()
              this.resetLiquidate()
              this.hideModal('modal-liquidate')
              this.alertSuccess({ text: this.$t('Your request to Liquidate Placement was successful') })
              this.showSpinner = false
            }
          } catch (error) {
            error.response.data.errors.forEach(responseError => {
              const reasonText = this.$t('Your request to Liquidate Placement failed. Reason')
              const errorMessage = `${reasonText}: ${responseError.errorMessage}`
              this.alertError({ text: errorMessage })
            })
          } finally {
            this.showSpinner = false
          }
        }
      })
    },
    resetLiquidate() {
      this.liquidatedPlacement.effectiveDate = ''
    },
    async fetchPlacement() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getPlacements({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.placementList = data.data.map(item => ({
            ...item,
            amount: toBalance(item.amount),
            startDate: toDateWithYear(item.startDate),
            maturityDate: toDateWithYear(item.maturityDate),
          }))
          this.totalCount = data.recordCount
        } else {
          this.placementList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    resetModal() {
      this.placementFormData = { ...null }
      this.placementFormData.daysPerAnnum = 365
      this.placementFormData.applyWithholdingTax = true
      this.isReadonly = false
      this.isLoading = false
      this.isDisabled = false
      this.isEditable = false
      this.isCustomDisabled = false
      this.modalTitle = this.$t('Create Placement')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchPlacement()
    },
  },
}
</script>
